import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, ApiError } from "../lib/api";
import { Banner, Button, Card, Checkbox, Field, PageBody, PageHeader, Select } from "../ui/Field";

type FieldDef = { name: string; label: string; kind: string; required?: boolean };

const KINDS = ["text", "rich_text", "markdown", "email", "url", "number", "boolean", "date", "datetime", "select", "asset", "reference"];

export default function CollectionNew() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [key, setKey] = useState("");
  const [name, setName] = useState("");
  const [namePlural, setNamePlural] = useState("");
  const [isTaxonomy, setIsTaxonomy] = useState(false);
  const [listingPath, setListingPath] = useState("");
  const [slugField, setSlugField] = useState("slug");
  const [fields, setFields] = useState<FieldDef[]>([
    { name: "title", label: "Title", kind: "text", required: true },
    { name: "slug", label: "Slug", kind: "text", required: true },
  ]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      const r = await api.post<{ id: string }>(`/api/sites/${slug}/collections`, {
        key, name, name_plural: namePlural,
        schema_json: fields, is_taxonomy: isTaxonomy,
        slug_field: slugField || null, listing_path: listingPath || null,
      });
      nav(`/sites/${slug}/collections/${r.id}`);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : (e as Error).message);
    } finally { setBusy(false); }
  }
  function updateField(i: number, patch: Partial<FieldDef>) { setFields((xs) => xs.map((f, j) => j === i ? { ...f, ...patch } : f)); }
  function addField() { setFields((xs) => [...xs, { name: `field_${xs.length + 1}`, label: "New field", kind: "text" }]); }
  function removeField(i: number) { setFields((xs) => xs.filter((_, j) => j !== i)); }

  return (
    <PageBody className="max-w-3xl">
      <PageHeader
        eyebrow="№ Collections · New"
        title={<>Define a <em>collection</em></>}
        description="Collections are typed content. A schema describes the shape of every entry."
      />
      {err && <Banner kind="error">{err}</Banner>}
      <form onSubmit={submit}>
        <Card number="C1" title="Basics">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Key" required value={key} onChange={(e) => setKey(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_"))} pattern="[a-z][a-z0-9_]*" placeholder="post" help="Lowercase snake_case." />
            <Field label="Slug field" value={slugField} onChange={(e) => setSlugField(e.target.value)} help="Which field is the URL slug." />
            <Field label="Name (singular)" required value={name} onChange={(e) => setName(e.target.value)} placeholder="Post" />
            <Field label="Name (plural)" required value={namePlural} onChange={(e) => setNamePlural(e.target.value)} placeholder="Posts" />
          </div>
          <Field label="Listing path" value={listingPath} onChange={(e) => setListingPath(e.target.value)} placeholder="/blog" help="If set, an index page is generated at this path." />
          <Checkbox label="Taxonomy (categories, tags)" checked={isTaxonomy} onChange={(e) => setIsTaxonomy(e.target.checked)} />
        </Card>

        <Card number="C2" title="Schema" description="The shape of each entry." className="mt-6">
          <ul className="space-y-2">
            {fields.map((f, i) => (
              <li key={i} className="grid grid-cols-[1fr_1fr_160px_90px_40px] gap-2 items-center">
                <input value={f.name} onChange={(e) => updateField(i, { name: e.target.value })} placeholder="name" className="h-8 px-2 text-[13px] font-mono border border-[var(--color-rule)] rounded-[3px] bg-[var(--color-surface)]" />
                <input value={f.label} onChange={(e) => updateField(i, { label: e.target.value })} placeholder="Label" className="h-8 px-2 text-[13px] border border-[var(--color-rule)] rounded-[3px] bg-[var(--color-surface)]" />
                <Select value={f.kind} onChange={(e) => updateField(i, { kind: e.target.value })} className="mb-0">
                  {KINDS.map((k) => <option key={k}>{k}</option>)}
                </Select>
                <label className="flex items-center gap-1.5 text-[12px] text-[var(--color-ink-2)]">
                  <input type="checkbox" checked={!!f.required} onChange={(e) => updateField(i, { required: e.target.checked })} />
                  required
                </label>
                <Button type="button" variant="ghost" size="sm" onClick={() => removeField(i)}>✕</Button>
              </li>
            ))}
          </ul>
          <Button type="button" variant="ghost" size="sm" onClick={addField} className="mt-3">+ Add field</Button>
        </Card>

        <div className="mt-6 flex items-center gap-2">
          <Button type="submit" disabled={busy}>{busy ? "Creating…" : "Create collection"}</Button>
          <Button type="button" variant="ghost" onClick={() => nav(`/sites/${slug}/collections`)}>Cancel</Button>
        </div>
      </form>
    </PageBody>
  );
}
